/**
 * Runway “Shop the look” drawer (`<ro-quick-add-modal-runway>`).
 * Depends on `ModalDialog` from `global.js`
 */

const RUNWAY_LG_QUERY = '(min-width: 1024px)';
/** Theme setting `shop_the_look_sidebar_layout`: contextual half-grid drawer (spatial layout). */
const RUNWAY_SIDEBAR_LAYOUT_SPATIAL = 'option_2';
/** Fallback for {@link getCssTransitionDurationMs} when the panel has no transition or is off-DOM. */
const RUNWAY_LAYOUT_DOM_CLEAR_MS_FALLBACK = 350;

/**
 * Returns the total CSS transition end time (duration + max delay) of `el` in milliseconds.
 * Takes the longest pair across all comma-separated transitions so the layout is cleared only
 * after every animated property has settled.
 * Falls back to `fallbackMs` when `el` is absent, off-DOM, or has no transition.
 * @param {HTMLElement | null | undefined} el
 * @param {number} [fallbackMs=0]
 * @returns {number}
 */
function getCssTransitionDurationMs(el, fallbackMs = 0) {
    if (!el) return fallbackMs;
    const style = getComputedStyle(el);
    const toSeconds = s => {
        s = s.trim();
        return s.endsWith('ms') ? parseFloat(s) / 1000 : parseFloat(s);
    };
    const maxOf = raw => raw.split(',').map(toSeconds).reduce((m, v) => (v > m ? v : m), 0);
    const total = maxOf(style.transitionDuration) + maxOf(style.transitionDelay);
    return total > 0 ? Math.round(total * 1000) : fallbackMs;
}

/**
 * Viewport height for fixed runway overlays (pair backdrop; option_2 half-empty slot height).
 * @returns {number}
 */
function runwayViewportHeight() {
    return Math.max(window.innerHeight || 0, document.documentElement?.clientHeight || 0);
}

if (!customElements.get('ro-quick-add-modal-runway')) {
    customElements.define(
        'ro-quick-add-modal-runway',
        class RoQuickAddModalRunway extends ModalDialog {
            constructor() {
                super();
            }

            /**
             * Whether Shop the look uses the spatial sidebar (`shop_the_look_sidebar_layout` option_2).
             * @returns {boolean}
             */
            get isRunwaySpatialLayout() {
                return this.dataset.runwaySidebarLayout === RUNWAY_SIDEBAR_LAYOUT_SPATIAL;
            }

            /**
             * Option 2 theme setting: drawer always docks on the right (`shop_the_look_option_2_sidebar_always_right`).
             * @returns {boolean}
             */
            get isRunwaySidebarAlwaysRight() {
                return this.dataset.runwaySidebarAlwaysRight === 'true';
            }

            /**
             * Maps contextual left-opening layouts to right-opening ones when `isRunwaySidebarAlwaysRight` is on.
             * @param {string} mode - Layout from `getRunwayLayoutDescriptor` (`pair-left`, `pair-right`, `half-empty-left`, …).
             * @returns {string}
             */
            resolveSpatialLayoutMode(mode) {
                if (!this.isRunwaySidebarAlwaysRight) return mode;
                if (mode === 'pair-right') return 'pair-left';
                if (mode === 'half-empty-left') return 'full';
                return mode;
            }

            connectedCallback() {
                super.connectedCallback();
                if (this.runwayUiBound) return;
                this.runwayUiBound = true;
                /** Shop the look option 2: single scrollable list with inline add-to-cart (no list/detail swap). */
                this.singleListLayout = this.isRunwaySpatialLayout;
                this.listView = this.querySelector('[data-runway-view="list"]');
                this.detailView = this.querySelector('[data-runway-view="detail"]');
                this.backBtn = this.querySelector('[data-runway-detail-back]');

                this.runwayBackdrop = this.querySelector('[data-runway-sidebar-backdrop]');
                this.runwayPanel = this.querySelector('.runway-shop-sidebar__panel');
                this._runwayPanels ??= this.querySelectorAll('[data-runway-panel]');

                this.runwayBackdrop?.addEventListener('click', () => this.hide(false));
                this.backBtn?.addEventListener('click', () => this.showListView());
                if (!this.singleListLayout) {
                    this.querySelectorAll('[data-runway-open-quickadd]').forEach(btn => {
                        btn.addEventListener('click', () => this.showDetailView(btn.dataset.runwayOpenQuickadd));
                    });
                }

                if (this.isRunwaySpatialLayout) {
                    this._runwayMediaResizeObserver = new ResizeObserver(() => {
                        if (!this.hasAttribute('open') || !this._runwayObservedMediaRoot) return;
                        this.clearRunwaySpatialLayout();
                        this.applyRunwaySpatialLayout(this._runwayObservedMediaRoot);
                    });
                }
            }

            /**
             * Disconnects the spatial resize observer, cancels pending layout clear, and reclaims `<html>` scroll
             * if the open drawer is removed (e.g. theme editor).
             * @returns {void}
             */
            disconnectedCallback() {
                this._runwayMediaResizeObserver?.disconnect();
                this._runwayObservedMediaRoot = null;
                clearTimeout(this._runwayClearLayoutTimer);
                this._runwayClearLayoutTimer = null;
                if (this._runwayScrollLockActive) {
                    this.unlockRunwayBackgroundScroll();
                }
            }

            /**
             * Resets runway drawer geometry (backdrop + panel slot) after a contextual layout.
             * Backdrop blur, opacity, and `pointer-events` for half-empty modes are in `ro-quick-add-modal-runway.css`.
             * @returns {void}
             */
            clearRunwaySpatialLayout() {
                this.removeAttribute('data-runway-layout');
                this.style.removeProperty('--rw-slot-t');
                this.style.removeProperty('--rw-slot-l');
                this.style.removeProperty('--rw-slot-w');
                this.style.removeProperty('--rw-slot-h');
                const bd = this.runwayBackdrop;
                if (bd) {
                    bd.style.removeProperty('top');
                    bd.style.removeProperty('left');
                    bd.style.removeProperty('width');
                    bd.style.removeProperty('height');
                    bd.style.removeProperty('right');
                    bd.style.removeProperty('bottom');
                    bd.style.removeProperty('position');
                }
                const panel = this.runwayPanel;
                if (panel) {
                    panel.style.removeProperty('top');
                    panel.style.removeProperty('left');
                    panel.style.removeProperty('width');
                    panel.style.removeProperty('height');
                    panel.style.removeProperty('position');
                }
            }

            /**
             * Runway shop-the-look layout from Liquid (`data-runway-layout-hint` on the media block).
             * @param {HTMLElement | null | undefined} mediaRoot - `#RoMedia…` wrapper for the clicked block.
             * @param {HTMLElement | null | undefined} [roMedia] - `#RoMedia` section root (one lookup per `applyRunwaySpatialLayout` pass).
             * @returns {{ mode: string, otherEl?: HTMLElement }}
             */
            getRunwayLayoutDescriptor(mediaRoot, roMedia) {
                if (!mediaRoot) return {mode: 'full'};
                if (!window.matchMedia(RUNWAY_LG_QUERY).matches) return {mode: 'full'};
                if (roMedia && !roMedia.contains(mediaRoot)) return {mode: 'full'};

                const hint = (mediaRoot.dataset.runwayLayoutHint || 'full').trim();
                if (!hint || hint === 'full') return {mode: 'full'};

                if (hint === 'pair-left' || hint === 'pair-right') {
                    const otherId = (mediaRoot.dataset.runwayPairNeighbor || '').trim();
                    if (!otherId) return {mode: 'full'};
                    const otherEl = document.getElementById(otherId);
                    if (!otherEl) return {mode: 'full'};
                    return {mode: hint, otherEl};
                }
                if (hint === 'half-empty-left' || hint === 'half-empty-right') {
                    return {mode: hint};
                }
                return {mode: 'full'};
            }

            /**
             * Positions the blurred backdrop over a rectangle (fixed). With `fullViewportHeight`, uses `rect`’s
             * horizontal span but top=0 and height=viewport (two-up runway).
             * @param {HTMLElement} el
             * @param {DOMRect} rect
             * @param {{ fullViewportHeight?: boolean }} [opts]
             * @returns {void}
             */
            positionRunwayBackdropRect(el, rect, opts = {}) {
                el.style.position = 'fixed';
                const fullVh = opts.fullViewportHeight === true;
                const vh = runwayViewportHeight();
                el.style.top = fullVh ? '0px' : `${Math.round(rect.top)}px`;
                el.style.left = `${Math.round(rect.left)}px`;
                el.style.width = `${Math.round(rect.width)}px`;
                el.style.height = fullVh ? `${Math.round(vh)}px` : `${Math.round(rect.height)}px`;
                el.style.right = 'auto';
                el.style.bottom = 'auto';
            }

            /**
             * Applies sidebar + backdrop rules (Shop the look option_2 only): full row blur, two-up sibling blur,
             * or empty-column slot with viewport-height panel and no backdrop blur.
             * @param {HTMLElement | null | undefined} mediaRoot - The `#RoMedia…` wrapper for the opener.
             * @returns {void}
             */
            applyRunwaySpatialLayout(mediaRoot) {
                if (!this.isRunwaySpatialLayout) return;
                const bd = this.runwayBackdrop;
                if (!bd || !this.runwayPanel || !mediaRoot) return;

                const roMedia = document.getElementById('RoMedia');
                const desc = this.getRunwayLayoutDescriptor(mediaRoot, roMedia);
                const rawMode = desc.mode;
                if (rawMode === 'full') return;

                const mode = this.resolveSpatialLayoutMode(rawMode);
                if (mode === 'full') return;

                this.dataset.runwayLayout = mode;

                const rowRect = roMedia?.getBoundingClientRect();
                const selfRect = mediaRoot.getBoundingClientRect();

                if (mode === 'pair-left' || mode === 'pair-right') {
                    const backdropEl =
                        this.isRunwaySidebarAlwaysRight && rawMode === 'pair-right'
                            ? mediaRoot
                            : desc.otherEl;
                    if (backdropEl) {
                        this.positionRunwayBackdropRect(bd, backdropEl.getBoundingClientRect(), {
                            fullViewportHeight: true,
                        });
                    }
                    return;
                }

                if (!rowRect) {
                    this.removeAttribute('data-runway-layout');
                    return;
                }

                const leftSlot = mode === 'half-empty-left';
                const slotW = leftSlot
                    ? Math.max(0, selfRect.left - rowRect.left)
                    : Math.max(0, rowRect.right - selfRect.right);
                const slotL = leftSlot ? rowRect.left : selfRect.right;
                const vh = runwayViewportHeight();
                this.style.setProperty('--rw-slot-t', '0px');
                this.style.setProperty('--rw-slot-l', `${Math.round(slotL)}px`);
                this.style.setProperty('--rw-slot-w', `${Math.round(slotW)}px`);
                this.style.setProperty('--rw-slot-h', `${Math.round(vh)}px`);
            }

            /**
             * Clears + reapplies spatial layout with transform transitions off (avoids animating 100%→-100% on dock swap).
             * @param {HTMLElement | null | undefined} mediaRoot
             * @returns {void}
             */
            syncRunwayLayoutBeforeOpen(mediaRoot) {
                if (!this.isRunwaySpatialLayout) return;
                const panel = this.runwayPanel;
                panel?.classList.add('runway-shop-sidebar__panel--suspend-transform-transition');
                this.clearRunwaySpatialLayout();
                this.applyRunwaySpatialLayout(mediaRoot);
                void panel?.offsetWidth;
                panel?.classList.remove('runway-shop-sidebar__panel--suspend-transform-transition');
            }

            /**
             * Product list + hero; hides quick-add panels and the back control (option_1 list state).
             * @returns {void}
             */
            showListView() {
                if (!this.listView) return;
                if (!this.singleListLayout && !this.detailView) return;
                this.setAttribute('data-runway-chrome', 'list');
                this.listView.classList.remove('tw-hidden');
                if (this.detailView) {
                    this.detailView.classList.add('tw-hidden');
                    this._runwayPanels?.forEach(panel => {
                        panel.classList.add('tw-hidden');
                    });
                }
                this.backBtn?.classList.add('tw-hidden');
                this.backBtn?.setAttribute('aria-hidden', 'true');
                this.backBtn?.setAttribute('tabindex', '-1');
            }

            /**
             * Shows one `ro-quick-add-product-panel` by id suffix (`data-runway-panel`); option_1 only.
             * @param {string} suffix - Value matching `data-runway-panel` (e.g. blockId-productId).
             * @returns {void}
             */
            showDetailView(suffix) {
                if (this.singleListLayout || !suffix || !this.listView || !this.detailView) return;
                const panel = this.querySelector(`[data-runway-panel="${suffix}"]`);
                if (!panel) return;

                this.setAttribute('data-runway-chrome', 'detail');
                this.listView.classList.add('tw-hidden');
                this.detailView.classList.remove('tw-hidden');
                this.backBtn?.classList.remove('tw-hidden');
                this.backBtn?.setAttribute('aria-hidden', 'false');
                this.backBtn?.removeAttribute('tabindex');

                this._runwayPanels?.forEach(p => {
                    p.classList.toggle('tw-hidden', p !== panel);
                });

                const focusTarget = panel.querySelector(
                    'button:not([disabled]), input:not([type="hidden"]):not([disabled]), [href], select',
                );
                focusTarget?.focus?.();
            }

            /**
             * Closes the dialog, schedules spatial layout clear for option_2, and returns focus to the cart UI when present.
             * @param {boolean} [preventFocus=false] - When true, `openedBy` is cleared so focus is not moved back to the opener.
             * @returns {void}
             */
            hide(preventFocus = false) {
                this.unlockRunwayBackgroundScroll();
                const cartNotification =
                    document.querySelector('cart-notification') || document.querySelector('cart-drawer');
                if (cartNotification) cartNotification.setActiveElement(this.openedBy);
                this.showListView();

                if (preventFocus) this.openedBy = null;
                super.hide();

                if (this.isRunwaySpatialLayout) {
                    this._runwayMediaResizeObserver?.disconnect();
                    this._runwayObservedMediaRoot = null;
                    clearTimeout(this._runwayClearLayoutTimer);
                    const clearMs = getCssTransitionDurationMs(this.runwayPanel, RUNWAY_LAYOUT_DOM_CLEAR_MS_FALLBACK);
                    this._runwayClearLayoutTimer = setTimeout(() => {
                        this.clearRunwaySpatialLayout();
                        this._runwayClearLayoutTimer = null;
                    }, clearMs);
                }
            }

            /**
             * Resolves Shopify Section Rendering API `section.id` for `ro-quick-add-content` so variant
             * updates use the correct `section_id` while each product keeps unique DOM `data-section`.
             * @param {HTMLElement} opener - The Shop the look button (has `data-handles` for the fetch).
             * @returns {Promise<void>}
             */
            async resolveRunwayQuickAddSectionIds(opener) {
                const panels = this.querySelectorAll('ro-product-info[data-runway-quick-add]');
                if (!panels.length || panels[0].dataset.apiSectionId) return;

                const handles = opener?.dataset?.handles?.trim();
                if (!handles) return;

                const firstHandle = handles.split(',')[0].trim();
                if (!firstHandle) return;

                const res = await fetch(
                    `${window.Shopify.routes.root}products/${encodeURIComponent(firstHandle)}/?sections=ro-quick-add-content`,
                );
                if (!res.ok) return;
                const data = await res.json();
                if (!data || typeof data !== 'object') return;
                const apiSectionId = Object.keys(data).find(key => data[key] != null);
                if (!apiSectionId) return;

                panels.forEach(el => {
                    el.dataset.apiSectionId = apiSectionId;
                });
            }

            /**
             * Locks page scroll on `<html>` while the runway drawer is open (`ModalDialog` already locks `body`).
             * @returns {void}
             */
            lockRunwayBackgroundScroll() {
                const root = document.documentElement;
                if (this._runwayScrollLockActive) return;
                this._runwayScrollLockActive = true;
                root.classList.add('ro-runway-scroll-lock');
            }

            /**
             * Restores `<html>` after `lockRunwayBackgroundScroll` (see `ro-runway-scroll-lock` in `ro-quick-add-modal-runway.css`).
             * @returns {void}
             */
            unlockRunwayBackgroundScroll() {
                const root = document.documentElement;
                if (!this._runwayScrollLockActive) return;
                this._runwayScrollLockActive = false;
                root.classList.remove('ro-runway-scroll-lock');
            }

            /**
             * Opens the Runway quick-add modal: resolves section ids, applies option_2 layout, then defers to `ModalDialog#show`.
             * @param {HTMLElement} opener - The control that opened the dialog (e.g. Shop the look `modal-opener` / button).
             * @returns {Promise<void>}
             */
            async show(opener) {
                opener.setAttribute('aria-disabled', true);
                try {
                    await this.resolveRunwayQuickAddSectionIds(opener);
                } catch (e) {
                    console.error('Runway quick add: could not resolve section id', e);
                } finally {
                    opener.removeAttribute('aria-disabled');
                }
                clearTimeout(this._runwayClearLayoutTimer);
                this._runwayClearLayoutTimer = null;

                this.showListView();
                const mediaRoot = opener?.closest?.('[id^="RoMedia"]');
                if (this.isRunwaySpatialLayout) {
                    this.syncRunwayLayoutBeforeOpen(mediaRoot);
                    if (this._runwayMediaResizeObserver) {
                        this._runwayMediaResizeObserver.disconnect();
                        this._runwayObservedMediaRoot = mediaRoot ?? null;
                        if (mediaRoot) this._runwayMediaResizeObserver.observe(mediaRoot);
                    }
                }

                super.show(opener);
                this.lockRunwayBackgroundScroll();
                if (this.isRunwaySpatialLayout) {
                    requestAnimationFrame(() => this.applyRunwaySpatialLayout(mediaRoot));
                }
            }
        },
    );
}
